import { readonly, ref } from 'vue'
import { postToDevice, type ConsoleKeepScreenOn } from './deviceBridge'

/**
 * What Tablet Browser says about the tablet and itself (Mark, 2026-10-16): the tablet's ID, the
 * one on the label on its back (FPO-TAB-001), the app's version, and what its camera scanner
 * reads.
 *
 * Asked with the bridge's `capabilities` command; the answer arrives as a `capabilities`
 * event (plugins/device-bridge.client.ts). The app knows the ID (the MDM's `tablet_id`
 * setting) and puts it in that answer from 2.21; before that the ID stays null here.
 *
 * `answered` false is the honest start, and what a desktop stays at. An app older than 2.15
 * does not answer at all, so it stays false there too: every feature below is then off.
 */

export type TabletInfo = {
  answered: boolean
  tabletId: string | null
  /** Whether the app sent `tabletId` at all (2.21 on): null then means none is set in the console. */
  tabletIdSent: boolean
  /** The tablet's Keep the Screen On (2.21 on; set in BOB from 2.24): `never`, `charging` or `always`; null before 2.21. */
  keepScreenOn: ConsoleKeepScreenOn | null
  appVersion: string
  /** The app's build number (2.21 is 23); null when it did not say. */
  versionCode: number | null
  /** What the app's camera scanner reads: `barcode`, `text`, `container`, `auto`. Empty with no camera. */
  cameraModes: readonly string[]
  /** The commands the app lists, if it ever lists them; then they outrank the version. */
  commands: readonly string[]
  /** What the app prints (Tablet Browser 2.22, `printCommands`): `print-raw`, `print-page`. Empty before 2.22. */
  printCommands: readonly string[]
  /** The ports `print-raw` sends to (2.22, `printRawPorts`, from BOB's tablet settings from 2.24). */
  printRawPorts: readonly number[]
  /**
   * Tablet Browser 2.24 on: the approved tablet's own short-lived token, sent with its log-in
   * sessions so BOB knows the call comes from that tablet (utils/tabletSession.ts). Null until a
   * manager approves the tablet in BOB, and from an older app. It lives an hour, so a session
   * asks the app afresh first.
   */
  tabletToken: string | null
}

type Optional = 'tabletIdSent' | 'keepScreenOn' | 'versionCode' | 'cameraModes' | 'commands' | 'printCommands' | 'printRawPorts' | 'tabletToken'
type Report = Omit<TabletInfo, 'answered' | Optional> & Partial<Pick<TabletInfo, Optional>>

const NOTHING: TabletInfo = { answered: false, tabletId: null, tabletIdSent: false, keepScreenOn: null, appVersion: '', versionCode: null, cameraModes: [], commands: [], printCommands: [], printRawPorts: [], tabletToken: null }

const info = ref<TabletInfo>({ ...NOTHING })
/** How many answers have come, so a fresh ask can tell the app's new answer from the last one. */
let answers = 0

export function reportTabletInfo(report: Report): void {
  info.value = {
    answered: true,
    tabletId: report.tabletId,
    tabletIdSent: report.tabletIdSent ?? report.tabletId !== null,
    keepScreenOn: report.keepScreenOn ?? null,
    appVersion: report.appVersion,
    versionCode: report.versionCode ?? null,
    cameraModes: [...(report.cameraModes ?? [])],
    commands: [...(report.commands ?? [])],
    printCommands: [...(report.printCommands ?? [])],
    printRawPorts: [...(report.printRawPorts ?? [])],
    tabletToken: report.tabletToken ?? null,
  }
  answers++
}

/** How many answers the app has given since the page loaded. */
export function tabletInfoAnswers(): number {
  return answers
}

/** Ask the app; false when there is no app to ask. */
export function askTabletInfo(): boolean {
  return postToDevice({ command: 'capabilities' })
}

export function useTabletInfo() {
  return readonly(info)
}

/** The current answer, outside a component. */
export function tabletInfoNow(): TabletInfo {
  return info.value
}

/**
 * Tablet Browser 2.21 (versionCode 23) is the first to take `keep-screen-on` (docs/tablets.md),
 * and the first to report the console's `keepScreenOn`, so either says so. An older app is never
 * sent it: it would only log a command it does not know, but the rule is to tell an app only
 * what it understands; the browser's wake lock carries on alone there.
 */
export const KEEP_SCREEN_ON_FROM_VERSION_CODE = 23

/** Whether the app takes the page's `keep-screen-on` command. */
export function appTakesKeepScreenOn(tablet: TabletInfo): boolean {
  if (!tablet.answered) return false
  if (tablet.commands.length > 0) return tablet.commands.includes('keep-screen-on')
  return tablet.keepScreenOn !== null || (tablet.versionCode ?? 0) >= KEEP_SCREEN_ON_FROM_VERSION_CODE
}

/** For tests. */
export function resetTabletInfo(): void {
  info.value = { ...NOTHING }
}

/** Whether the app takes this print command (2.22 lists them in `printCommands`; an older app none). */
export function appPrints(tablet: TabletInfo, command: 'print-raw' | 'print-page'): boolean {
  return tablet.answered && tablet.printCommands.includes(command)
}
